import { useState, useCallback, useRef, useEffect } from 'react';
import styles from './DatePicker.module.css';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * 自定义日期选择器，替代原生 type="date"
 */
export const DatePicker: React.FC<DatePickerProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      const d = new Date(value + 'T00:00:00');
      return { year: d.getFullYear(), month: d.getMonth() };
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const containerRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const formatDate = useCallback((d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);

  const displayText = value ? value : '选择日期';

  const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
  const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

  const daysInMonth = new Date(viewDate.year, viewDate.month + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewDate.year, viewDate.month, 1).getDay();

  const prevMonth = () => {
    setViewDate((v) => {
      let { year, month } = v;
      month--;
      if (month < 0) { month = 11; year--; }
      return { year, month };
    });
  };

  const nextMonth = () => {
    setViewDate((v) => {
      let { year, month } = v;
      month++;
      if (month > 11) { month = 0; year++; }
      return { year, month };
    });
  };

  const handleSelect = (day: number) => {
    const selected = new Date(viewDate.year, viewDate.month, day);
    onChange(formatDate(selected));
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
  };

  // 构建日历格子
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedDate = value ? new Date(value + 'T00:00:00') : null;
  const isToday = (day: number) => {
    const now = new Date();
    return day === now.getDate() && viewDate.month === now.getMonth() && viewDate.year === now.getFullYear();
  };
  const isSelected = (day: number) => {
    return selectedDate && day === selectedDate.getDate() &&
      viewDate.month === selectedDate.getMonth() && viewDate.year === selectedDate.getFullYear();
  };

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <div
        className={`${styles.trigger} ${isOpen ? styles.open : ''}`}
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className={displayText === '选择日期' ? styles.placeholder : ''}>{displayText}</span>
        <span className={styles.arrow}>{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          {/* Header */}
          <div className={styles.calHeader}>
            <button type="button" className={styles.navBtn} onClick={prevMonth}>&#8249;</button>
            <span className={styles.monthLabel}>{viewDate.year}年{monthNames[viewDate.month]}</span>
            <button type="button" className={styles.navBtn} onClick={nextMonth}>&#8250;</button>
          </div>

          {/* Day names */}
          <div className={styles.dayNames}>
            {dayNames.map((d) => (
              <span key={d} className={styles.dayName}>{d}</span>
            ))}
          </div>

          {/* Calendar grid */}
          <div className={styles.grid}>
            {cells.map((day, i) => (
              <button
                key={i}
                type="button"
                className={`${styles.cell} ${day === null ? styles.empty : ''} ${day && isSelected(day) ? styles.selected : ''} ${day && isToday(day) ? styles.today : ''}`}
                onClick={() => day && handleSelect(day)}
              >
                {day}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className={styles.calFooter}>
            <button type="button" className={styles.clearBtn} onClick={handleClear}>清除</button>
            <button type="button" className={styles.todayBtn} onClick={() => {
              const now = new Date();
              setViewDate({ year: now.getFullYear(), month: now.getMonth() });
              handleSelect(now.getDate());
            }}>今天</button>
          </div>
        </div>
      )}
    </div>
  );
};
